import { Button, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { IConfigVersion, IState, ITemplate } from "store/types";
import styled from "styled-components";
import AutoFixHigh from "@mui/icons-material/AutoFixHigh";
import { Stack } from "components/Layout";
import AlignmentToolIcon from "../alignmentTool/images/landing.png";
import { navigate } from "@reach/router";
import { getTemplates } from "actions/templates";
import { getSubjects } from "actions/subjects";
import Spinner from "components/spinner";
import { StringParam, useQueryParams } from "use-query-params";
import { getCurrentSubjectCategory } from "lib/currentSubject";
import { grey2, grey3 } from "theme/colors";
import { getConfig } from "actions/config";
import { sortBy } from "lodash";
import { isAdminUser } from "lib/utils";
import { SessionTimeDialog } from "containers/SessionTimeDialog";
import { getSessionTimeLeftMinutes } from "lib/Cookies";
import { requiredEnvVar } from "lib/envUtil";

const PageTitle = styled.h2`
  margin-top: 0.5rem;
  margin-bottom: 0rem;
  letter-spacing: -1px;
  align-items: center;
  text-align: center;
`;

const PageDescription = styled.p`
  margin-top: 1rem;
  margin-bottom: 2rem;
  align-items: center;
  text-align: center;
`;

const RunButton = styled(Button)`
  margin-top: 1rem;
`;

export const TemplateSelectionPage = () => {
  const dispatch = useDispatch();

  // Query parameters - treat as strings so MUI doesn't get upset about invalid Select values
  const [query, setQuery] = useQueryParams({
    subjectId: StringParam,
    templateId: StringParam,
    ruleId: StringParam,
  });

  // Load data from Moodle
  useEffect(() => {
    dispatch(getConfig());
    dispatch(getSubjects());
    dispatch(getTemplates());
  }, [dispatch]);

  const [selectedSubject, setSelectedSubject] = useState(query.subjectId || "");
  const [selectedTemplate, setSelectedTemplate] = useState(query.templateId || "");
  const [selectedRule, setSelectedRule] = useState(query.ruleId || "");
  const [showSessionDialog, setShowSessionDialog] = useState(false);

  // What we need from Redux
  const subjects = useSelector((state: IState) => state.subjects);
  const templates = useSelector((state: IState) => state.templates);
  const config = useSelector((state: IState) => state.config);
  const loading = useSelector(
    (state: IState) => state.loadingSubjects || state.loadingTemplates || state.loadingConfig,
  );

  const disableSubjectSelection = loading || subjects.length === 0;
  const disableTemplateSelection = loading || templates.length === 0;
  const hideTemplateSelection = selectedSubject.length === 0;
  const hideRuleSelection = config && config.adminIds ? !isAdminUser(config.adminIds) : true;
  const disableRunButton = selectedSubject.length === 0 || selectedTemplate.length === 0;
  const subjectCategory = getCurrentSubjectCategory(Number(selectedSubject));

  const onSubjectChange = useCallback(
    (e: any) => {
      setQuery({
        subjectId: e.target.value,
        templateId: undefined,
        ...(!hideRuleSelection && { ruleId: query.ruleId }),
      });
      setSelectedSubject(e.target.value);
      setSelectedTemplate("");
    },
    [hideRuleSelection, query.ruleId, setQuery],
  );

  const onTemplateChange = useCallback(
    (e: any) => {
      setQuery({
        subjectId: query.subjectId,
        templateId: e.target.value,
        ...(!hideRuleSelection && { ruleId: query.ruleId }),
      });
      setSelectedTemplate(e.target.value);
    },
    [hideRuleSelection, query.ruleId, query.subjectId, setQuery],
  );

  const onRuleChange = useCallback(
    (e: any) => {
      setQuery({
        subjectId: query.subjectId,
        templateId: query.templateId,
        ruleId: e.target.value,
      });
      setSelectedRule(e.target.value);
    },
    [query.subjectId, query.templateId, setQuery],
  );

  const onRunButtonClick = useCallback(() => {
    query.ruleId
      ? navigate(`/wizardStepTwo?subjectId=${selectedSubject}&templateId=${selectedTemplate}&ruleId=${selectedRule}`)
      : navigate(`/wizardStepTwo?subjectId=${selectedSubject}&templateId=${selectedTemplate}`);
  }, [query.ruleId, selectedRule, selectedSubject, selectedTemplate]);

  const sortRuleVersions = useCallback(() => {
    if (config && config.versions) {
      const active = config.versions.find((version: IConfigVersion) => version.version === config.currentVersion);
      if (active) {
        const inactive = config.versions.filter((version: IConfigVersion) => version.version !== config.currentVersion);
        const inactiveSortedByMostRecent = sortBy(inactive, "version").reverse();
        return [active, ...inactiveSortedByMostRecent];
      }
      return config.versions;
    }
    return [];
  }, [config]);

  if (loading) {
    return <Spinner loading={loading} />;
  } else
    return (
      <Stack>
        <img
          src={AlignmentToolIcon}
          style={{ alignSelf: "center", paddingTop: "72px", paddingBottom: "16" }}
          width="252"
          height="168"
          alt="LMSMorph icon"
        />
        <PageTitle>Update your site with LMSMorph</PageTitle>
        <PageDescription>
          Use LMSMorph to effortlessly align the structure of your
          <br /> existing site with our pre-defined templates.
          <br />
          <br />
          Only Templates in the same Category as your Site in the LMS will be selectable.
          <br />
          <br /> Rest assured, you&apos;ll have the opportunity to review, edit and approve all <br />
          changes before they&apos;re applied.
        </PageDescription>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div>
            <InputLabel
              id="subject-selector-label"
              htmlFor="subject-selector"
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                textAlign: "left",
              }}
            >
              Site to update
            </InputLabel>
            <FormControl>
              <Select
                sx={{
                  width: "700px",
                  maxWidth: "700px",
                  "&.Mui-focused": {
                    border: `solid 2px #252525`,
                  },
                }}
                variant="outlined"
                id="subject-selector"
                disabled={disableSubjectSelection}
                value={selectedSubject}
                defaultValue=""
                onChange={onSubjectChange}
                displayEmpty
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  sx: {
                    "& .MuiMenu-paper": {
                      backgroundColor: "white",
                      color: "text.light",
                    },
                    "& .MuiMenuItem-root:hover": {
                      backgroundColor: grey2.toString(),
                      color: "text.white",
                    },
                    "& .Mui-selected": {
                      backgroundColor: grey3.toString(),
                      color: "text.white",
                    },
                  },
                }}
              >
                {subjects.map((subject) => (
                  <MenuItem key={subject.id} value={subject.id}>
                    {subject.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>
        &nbsp;
        {!hideTemplateSelection && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <div>
              <InputLabel id="template-selector-label" htmlFor="template-selector">
                Select a template
              </InputLabel>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <FormControl>
                  <Select
                    sx={{
                      width: "700px",
                      maxWidth: "700px",
                      "&.Mui-focused": {
                        border: `solid 2px #252525`,
                      },
                    }}
                    variant="outlined"
                    id="template-selector"
                    disabled={disableTemplateSelection}
                    value={selectedTemplate}
                    defaultValue=""
                    onChange={onTemplateChange}
                    displayEmpty
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      sx: {
                        "& .MuiMenu-paper": {
                          backgroundColor: "white",
                          color: "text.light",
                        },
                        "& .MuiMenuItem-root:hover": {
                          backgroundColor: grey2.toString(),
                          color: "text.white",
                        },
                        "& .Mui-selected": {
                          backgroundColor: grey3.toString(),
                          color: "text.white",
                        },
                      },
                    }}
                  >
                    {templates
                      .filter((template: ITemplate) => template.categoryId === subjectCategory)
                      .map((template: ITemplate) => (
                        <MenuItem key={template.id} value={template.id}>
                          {template.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </div>
            </div>
            &nbsp;
            {!hideRuleSelection && (
              <div>
                <InputLabel id="rule-selector-label" htmlFor="rule-selector">
                  Rule version
                </InputLabel>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <FormControl>
                    <Select
                      sx={{
                        width: "700px",
                        maxWidth: "700px",
                        "&.Mui-focused": {
                          border: `solid 2px #252525`,
                        },
                      }}
                      variant="outlined"
                      id="rule-selector"
                      value={selectedRule}
                      defaultValue=""
                      onChange={onRuleChange}
                      displayEmpty
                      MenuProps={{
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        sx: {
                          "& .MuiMenu-paper": {
                            backgroundColor: "white",
                            color: "text.light",
                          },
                          "& .MuiMenuItem-root:hover": {
                            backgroundColor: grey2.toString(),
                            color: "text.white",
                          },
                          "& .Mui-selected": {
                            backgroundColor: grey3.toString(),
                            color: "text.white",
                          },
                        },
                      }}
                    >
                      {sortRuleVersions().map((version: IConfigVersion) => (
                        <MenuItem key={version.version} value={version.version}>
                          {config.currentVersion === version.version ? `${version.version} (Active)` : version.version}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
            )}
          </div>
        )}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <RunButton
            startIcon={<AutoFixHigh />}
            variant="contained"
            disabled={disableRunButton}
            onClick={() =>
              getSessionTimeLeftMinutes() > Number(requiredEnvVar("MIN_ALIGN_TIME"))
                ? onRunButtonClick()
                : setShowSessionDialog(true)
            }
          >
            Run Aligner
          </RunButton>
          <SessionTimeDialog
            open={showSessionDialog}
            onConfirm={onRunButtonClick}
            onClose={() => {
              setShowSessionDialog(false);
            }}
          />
        </div>
      </Stack>
    );
};
